// The Activities dashboard for an area made on the catalogue 1.0 maps (admin levels and the model grid), ported from
// the demo build. It draws one selection result (utils/eeActivities.ts EeJobResult): each counted area or cell in a
// colour by its value (grid cells also labelled with it), the per-hour result, and the distributions of arrivals.
// The result carries every hour, so changing the shown hour needs no new request. The date, the resolution and the
// hour all come from the data. Coverage lines are drawn only when the result has a coverage, which the data API's
// results do not (utils/catalogueActivities.ts).
import React, { useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, BarElement, CategoryScale, Legend, LinearScale, Title, Tooltip } from 'chart.js';
import NavBar from '../../components/common/NavBar';
import RealtimeAreaMap from '../../components/common/RealtimeAreaMap';
import type { AreaPolygon } from '../../components/common/RealtimeAreaMap';
import AboutThisData from '../../components/data/AboutThisData';
import SourceBadge from '../../components/data/SourceBadge';
import { COUNTRIES, DEFAULT_COUNTRY, normalizeCountry } from '../../config/countries';
import type { CountryCode } from '../../config/countries';
import { GRANULARITY_LABELS, MEASURES, isActivityMeasure, offeredActivityMeasures } from '../../config/products';
import type { ActivityMeasure, MeasureKey } from '../../config/products';
import { useThemeColors } from '../../hooks/useThemeColors';
import type { ThemeColors } from '../../config/theme';
import { readShape } from '../../geo/areaShapes';
import type { AreaShape } from '../../geo/areaShapes';
import { mapsApi } from '../../services/mapsApi';
import type { MapGeometry } from '../../services/mapsApi';
import { formatDayLabel } from '../../utils/dateWindow';
import {
  EE_MEASURES,
  aboutText,
  averageText,
  countText,
  coverageLine,
  eeRowValue,
  eeSummaryValue,
  hourlyDistributionArrivals,
  isEeGridType,
  publishedHoursText,
  eeValueText,
  modeBands,
  panelBands,
  selectDistribution,
  shareText,
  utcWithLocal,
} from '../../utils/eeActivities';
import type { EeDistributionBand, EeDistributionGroup, EeDistributionMeasure, EeJobResult, EeSummary } from '../../utils/eeActivities';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/** The most ids one by-ids request carries. */
const BY_IDS_CHUNK = 500;

const heatColor = (colors: ThemeColors, t: number) => colors.css('brand-700', 0.15 + 0.85 * Math.max(0, Math.min(1, t)));

const labelOf = (m: ActivityMeasure) => MEASURES[m].label;

const Stat: React.FC<{ label: string; value: string; detail?: string }> = ({ label, value, detail }) => (
  <div role="group" aria-label={label} className="bg-surface rounded-lg shadow p-4">
    <div className="text-xs text-ink-subtle">{label}</div>
    <div className="text-2xl font-semibold text-ink">{value}</div>
    {detail ? <div className="text-xs text-ink-subtle mt-1">{detail}</div> : null}
  </div>
);

const MapLegend: React.FC = () => (
  <div className="rounded-lg border border-paper-300 bg-surface/95 px-3 py-2 text-xs text-ink shadow-soft">
    <div>Colour: the counted area's value (darker = more)</div>
  </div>
);

// Everyone is the sum of the four roles; each role is modelled.
const GROUPS: { key: EeDistributionGroup; label: string; place: string }[] = [
  { key: 'everyone', label: 'Everyone arriving', place: '' },
  { key: 'home', label: 'Arriving home (modelled)', place: ' at home' },
  { key: 'work', label: 'Arriving at their workplace (modelled)', place: ' at a workplace' },
  { key: 'school', label: 'Arriving at school (modelled)', place: ' at school' },
  { key: 'elsewhere', label: 'Arriving elsewhere (modelled)', place: ' elsewhere' },
];
const groupPlace = (g: EeDistributionGroup) => GROUPS.find((x) => x.key === g)?.place ?? '';

/** Bands as a horizontal bar chart of each band's share of the arrivals. */
const BandsPanel: React.FC<{
  title: string;
  average?: string;
  bands: EeDistributionBand[];
  empty: string;
  colors: ThemeColors;
}> = ({ title, average, bands, empty, colors }) => (
  <section aria-label={title} className="bg-surface rounded-lg shadow p-4 min-w-0">
    <h3 className="text-base font-semibold text-ink">{title}</h3>
    {average ? <p className="text-xs text-ink-subtle">{average}</p> : null}
    {bands.length > 0 ? (
      <div className="mt-3" style={{ height: Math.max(160, 28 * bands.length + 50) }}>
        <Bar
          data={{
            labels: bands.map((b) => b.name),
            datasets: [
              {
                label: 'Share of arrivals (%)',
                data: bands.map((b) => (b.share === null ? 0 : b.share * 100)),
                backgroundColor: colors.css('brand-500'),
              },
            ],
          }}
          plugins={[
            {
              // Writes each band's share at the end of its bar, so it is readable without hovering.
              id: 'bandValueLabels',
              afterDatasetsDraw: (chart) => {
                const { ctx } = chart;
                const meta = chart.getDatasetMeta(0);
                ctx.save();
                ctx.fillStyle = colors.chart.tick;
                ctx.font = '11px sans-serif';
                ctx.textAlign = 'left';
                ctx.textBaseline = 'middle';
                meta.data.forEach((el, i) => {
                  const b = bands[i];
                  if (b) ctx.fillText(shareText(b.share), el.x + 4, el.y);
                });
                ctx.restore();
              },
            },
          ]}
          options={{
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            color: colors.chart.tick,
            layout: { padding: { right: 40 } },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (item) => {
                    const b = bands[item.dataIndex];
                    return b ? [b.arrivals === null ? '' : `${b.arrivals.toLocaleString()} arrivals`, shareText(b.share)].filter(Boolean).join(' · ') : '';
                  },
                },
              },
            },
            scales: {
              x: { ticks: { color: colors.chart.tick, callback: (v) => `${v}%` }, grid: { color: colors.chart.grid } },
              y: { ticks: { color: colors.chart.tick, autoSkip: false }, grid: { color: colors.chart.grid } },
            },
          }}
        />
      </div>
    ) : (
      <p className="mt-3 text-sm text-ink-muted">{empty}</p>
    )}
  </section>
);

/** One distribution of the answer as a bands panel. */
const DistributionPanel: React.FC<{
  title: string;
  average?: string;
  ee: EeJobResult;
  hour: number | null;
  group: EeDistributionGroup;
  measure: EeDistributionMeasure;
  colors: ThemeColors;
}> = ({ title, average, ee, hour, group, measure, colors }) => {
  const dist = selectDistribution(ee.distributions, hour, group, measure);
  return (
    <BandsPanel
      title={title}
      average={group === 'everyone' ? average : undefined}
      bands={dist ? panelBands(dist) : []}
      empty={`No modelled arrivals${groupPlace(group)}${hour === null ? '' : ' in this hour'}.`}
      colors={colors}
    />
  );
};

const MODE_TITLE = 'Arrivals by mode (modelled)';

/** The arrivals by mode of the shown hour (or the day). The modes are not split by role, so a role gets a note. */
const ModePanel: React.FC<{ summary: EeSummary | undefined; group: EeDistributionGroup; colors: ThemeColors }> = ({ summary, group, colors }) =>
  group !== 'everyone' ? (
    <section aria-label={MODE_TITLE} className="bg-surface rounded-lg shadow p-4 min-w-0">
      <h3 className="text-base font-semibold text-ink">{MODE_TITLE}</h3>
      <p className="mt-3 text-sm text-ink-muted">Modes are not published by role.</p>
    </section>
  ) : (
    <BandsPanel title={MODE_TITLE} bands={modeBands(summary)} empty="No modelled arrivals by mode." colors={colors} />
  );

export interface EeDashboardState {
  ee?: EeJobResult;
  selectedGrids?: MapGeometry[];
  measure?: string;
  countryCode?: string;
}

const EeActivityDashboard: React.FC<{ state: EeDashboardState }> = ({ state }) => {
  const colors = useThemeColors();
  const ee = state.ee as EeJobResult;
  const country: CountryCode = normalizeCountry(state.countryCode) ?? DEFAULT_COUNTRY;
  const zone = COUNTRIES[country].timeZone;
  // What the map and the by-hour chart show. The answer carries every measure, so this is only a choice of display;
  // a measure the answer has no figure for (stays) is not offered.
  const measureOptions = useMemo(
    () => offeredActivityMeasures(country, ee.geo_type).filter((m) => EE_MEASURES.includes(m)),
    [country, ee.geo_type],
  );
  const [measure, setMeasure] = useState<ActivityMeasure>(() =>
    isActivityMeasure(state.measure as MeasureKey) && measureOptions.includes(state.measure as ActivityMeasure)
      ? (state.measure as ActivityMeasure)
      : 'sum_unique_people',
  );
  const measureLabel = labelOf(measure);
  // The catalogue's model grid: a selection can hold any cell size, so each counted cell is drawn from its own outline.
  const isGrid = isEeGridType(ee.geo_type);
  const hourly = ee.type === 'hourly';
  const areaWord = isGrid ? 'cell' : 'area';
  const summedPeople = ee.people_is_sum_of_areas === true;
  const busiest = ee.busiest_time;
  // Several areas' people run over the true number, so with the busiest time beside them they are rounded visitors.
  const visitors = summedPeople && busiest !== undefined;
  const summedLabel = `${labelOf('sum_unique_people')} (sum of ${areaWord}s)`;
  const peopleLabel = visitors ? (hourly ? 'Visitors this hour' : 'Visitors during the day') : summedPeople ? summedLabel : labelOf('sum_unique_people');
  const hourChartLabel = measure !== 'sum_unique_people' ? measureLabel : visitors ? 'Visitors' : peopleLabel;
  const howToRead = [
    ...(busiest ? [{ term: 'People at the busiest time', text: 'the average number of people here during the busiest hour.' }] : []),
    ...(summedPeople
      ? [{ term: 'Visitors', text: `people who stopped here for at least 20 minutes. We count each ${areaWord} separately, so someone who visited two ${areaWord}s counts twice. This matters little for a small area and more for a large one.` }]
      : []),
    { term: 'Arrivals', text: 'trips ending here. One person can arrive more than once.' },
  ];

  const hours = useMemo(() => ee.summary.map((s) => s.hour_of_day), [ee.summary]);
  const [hour, setHour] = useState<number | null>(() => {
    // Open on the busiest hour, so the map shows something worth looking at.
    if (!hourly) return null;
    let best: number | null = null;
    let top = -1;
    for (const s of ee.summary) {
      const v = eeSummaryValue(s, 'sum_unique_people') ?? -1;
      if (v > top) {
        top = v;
        best = s.hour_of_day;
      }
    }
    return best ?? ee.summary[0]?.hour_of_day ?? null;
  });
  const summary = ee.summary.find((s) => s.hour_of_day === hour) ?? ee.summary[0];
  const [group, setGroup] = useState<EeDistributionGroup>('everyone');
  const distHour = summary?.hour_of_day ?? null;
  // How far the hours with published arrivals fall short of the day, for the selected group.
  const hoursSum = hourlyDistributionArrivals(ee.distributions, group);
  const dayFigure = ee.day_arrivals?.[group];

  // The outline of every cell or area in the answer: the selected ones came with the page; on the grid, a counted
  // cell the page did not bring is fetched from the catalogue maps.
  const [extra, setExtra] = useState<MapGeometry[]>([]);
  const known = useMemo(() => new Set((state.selectedGrids ?? []).map((g) => g.geo_id)), [state.selectedGrids]);
  useEffect(() => {
    if (!isGrid) return;
    const wanted = [...ee.requestedIds, ...ee.rows.filter((r) => r.role === 'counted').map((r) => r.geo_id)];
    const missing = Array.from(new Set(wanted)).filter((id) => !known.has(id));
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const found: MapGeometry[] = [];
      for (let i = 0; i < missing.length; i += BY_IDS_CHUNK) {
        const res = await mapsApi.getGeosByIds(ee.geo_type as 'model_grid', missing.slice(i, i + BY_IDS_CHUNK), country, true);
        found.push(...res.results);
      }
      if (!cancelled) setExtra(found);
    })().catch(() => {
      // Those cells stay undrawn; the figures do not depend on them.
    });
    return () => {
      cancelled = true;
    };
  }, [ee.requestedIds, ee.rows, ee.geo_type, isGrid, known, country]);

  const shapes = useMemo(() => {
    const out = new Map<string, AreaShape>();
    for (const g of [...(state.selectedGrids ?? []), ...extra]) {
      const shape = readShape(g.geometry);
      if (shape) out.set(g.geo_id, shape);
    }
    return out;
  }, [state.selectedGrids, extra]);

  const polygons: AreaPolygon[] = useMemo(() => {
    const out: AreaPolygon[] = [];
    const idle = colors.css('series-10', 0.3);
    const rowsAt = ee.rows.filter((r) => r.hour_of_day === (summary?.hour_of_day ?? null));

    if (!isGrid) {
      const values = rowsAt.map((r) => eeRowValue(r, measure)).filter((v): v is number => v !== null);
      const lo = values.length ? Math.min(...values) : 0;
      const hi = values.length ? Math.max(...values) : 1;
      const byId = new Map(rowsAt.map((r) => [r.geo_id, r]));
      shapes.forEach((shape, id) => {
        const row = byId.get(id);
        const v = row ? eeRowValue(row, measure) : null;
        out.push({ id, name: row?.geo_name ?? id, wkt: shape, color: v === null ? idle : heatColor(colors, hi > lo ? (v - lo) / (hi - lo) : 0.7) });
      });
      return out;
    }

    // Each counted cell in its own outline, labelled with its value.
    const counted = rowsAt.filter((r) => r.role === 'counted');
    const values = counted.map((r) => eeRowValue(r, measure)).filter((v): v is number => v !== null);
    const lo = values.length ? Math.min(...values) : 0;
    const hi = values.length ? Math.max(...values) : 1;
    for (const r of counted) {
      const shape = shapes.get(r.geo_id);
      if (!shape) continue;
      const v = eeRowValue(r, measure);
      out.push({
        id: `cell-${r.geo_id}`,
        name: r.geo_id,
        wkt: shape,
        color: v === null ? idle : heatColor(colors, hi > lo ? (v - lo) / (hi - lo) : 0.7),
        label: eeValueText(v, measure),
      });
    }
    return out;
  }, [ee, summary, shapes, isGrid, measure, colors]);

  const hourLabels = hours.map((h) => (h === null ? '' : utcWithLocal(Date.parse(`${ee.date}T${String(h).padStart(2, '0')}:00:00Z`), zone)));

  return (
    <div className="min-h-screen bg-paper-200">
      <NavBar title="Activity Dashboard" showBackButton={true} />
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex flex-wrap items-center gap-2 text-sm text-ink-muted">
          <span>
            {formatDayLabel(ee.date)} · {GRANULARITY_LABELS[ee.type]} · UTC
          </span>
          <SourceBadge country={country} product="activities" />
        </div>

        {summary ? (
          <div className={`grid grid-cols-2 gap-4 ${busiest ? 'md:grid-cols-3 xl:grid-cols-6' : 'md:grid-cols-5'}`}>
            {busiest ? (
              <Stat
                label="People at the busiest time"
                value={`About ${aboutText(busiest.people, 3)}`}
                detail={utcWithLocal(Date.parse(`${ee.date}T${String(busiest.hour).padStart(2, '0')}:00:00Z`), zone)}
              />
            ) : null}
            <Stat label={peopleLabel} value={visitors ? `About ${aboutText(summary.people, 2)}` : countText(summary.people)} />
            <Stat label={MEASURES.arrivals.label} value={countText(summary.arrivals)} />
            <Stat label={`${MEASURES.average_distance.label} (km)`} value={averageText(summary.average_distance)} />
            <Stat label={`${MEASURES.average_travel_time.label} (min)`} value={averageText(summary.average_travel_time)} />
            <Stat label={`${MEASURES.average_visit_length.label} (min)`} value={averageText(summary.average_visit_length)} />
          </div>
        ) : (
          <p className="text-sm text-ink-muted">No published figures for this selection.</p>
        )}
        <AboutThisData country={country} product="activities" howToRead={howToRead} />
        {isGrid && summary?.coverage != null ? (
          <p className="text-sm text-ink">
            {coverageLine(summary.coverage, summary.uncovered_cells, summary.uncovered_km2 ?? 0)}
            {ee.addedCells > 0 ? ` · Includes ${ee.addedCells.toLocaleString()} cells added to complete the result` : ''}
          </p>
        ) : null}

        <div className="bg-surface rounded-lg shadow p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-ink">{measureLabel} by {isGrid ? 'cell' : 'area'}</h2>
              <p className="text-xs text-ink-subtle mb-3">
                {hourly && summary?.hour_of_day != null
                  ? `At ${utcWithLocal(Date.parse(`${ee.date}T${String(summary.hour_of_day).padStart(2, '0')}:00:00Z`), zone)}`
                  : formatDayLabel(ee.date)}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-ink">
              Show on map
              <select
                className="select select-sm select-bordered"
                value={measure}
                onChange={(e) => setMeasure(e.target.value as ActivityMeasure)}
              >
                {measureOptions.map((m) => (
                  <option key={m} value={m}>
                    {labelOf(m)}
                  </option>
                ))}
              </select>
            </label>
            {hourly ? (
              <label className="flex items-center gap-2 text-sm text-ink">
                Hour
                <select
                  className="select select-sm select-bordered"
                  value={hour ?? ''}
                  onChange={(e) => setHour(Number(e.target.value))}
                >
                  {hours.map((h, i) => (
                    <option key={h ?? 'day'} value={h ?? ''}>
                      {hourLabels[i]}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            </div>
          </div>
          <div style={{ height: 420 }}>
            <RealtimeAreaMap polygons={polygons} fillOpacity={0.6} overlayBottomLeft={<MapLegend />} />
          </div>
        </div>

        {hourly ? (
          <div className="bg-surface rounded-lg shadow p-4">
            <h2 className="text-base font-semibold text-ink">{hourChartLabel} by hour</h2>
            <p className="text-xs text-ink-subtle mb-3">The selection result for each hour (UTC, local time in brackets).</p>
            <div style={{ height: 300 }}>
              <Bar
                data={{
                  labels: hourLabels,
                  datasets: [
                    {
                      label: hourChartLabel,
                      data: ee.summary.map((s) => eeSummaryValue(s, measure)),
                      backgroundColor: colors.css('brand-500'),
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  color: colors.chart.tick,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: { ticks: { color: colors.chart.tick }, grid: { color: colors.chart.grid } },
                    y: { ticks: { color: colors.chart.tick }, grid: { color: colors.chart.grid } },
                  },
                }}
              />
            </div>
          </div>
        ) : null}

        {(ee.distributions ?? []).length === 0 && ee.distributions_unavailable ? (
          <section aria-label="Distributions of arrivals" className="bg-surface rounded-lg shadow p-4">
            <h2 className="text-base font-semibold text-ink">Distributions of arrivals</h2>
            <p className="mt-2 text-sm text-ink-muted">Not available. {ee.distributions_unavailable}</p>
          </section>
        ) : null}

        {(ee.distributions ?? []).length > 0 ? (
          <div className="space-y-4">
            <div>
              <h2 className="text-base font-semibold text-ink">Distributions of arrivals</h2>
              <p className="text-xs text-ink-subtle mb-2">
                {hourly && distHour !== null
                  ? `At ${utcWithLocal(Date.parse(`${ee.date}T${String(distHour).padStart(2, '0')}:00:00Z`), zone)}`
                  : formatDayLabel(ee.date)}
              </p>
              {/* The distributions cover the same counted cells as the totals above. */}
              {isGrid && summary?.coverage != null ? (
                <p className="text-xs text-ink-muted mb-2">
                  {coverageLine(summary.coverage, summary.uncovered_cells, summary.uncovered_km2 ?? 0)}
                </p>
              ) : null}
              <div role="group" aria-label="Group" className="join max-w-full overflow-x-auto">
                {GROUPS.map((g) => (
                  <button
                    key={g.key}
                    type="button"
                    aria-pressed={group === g.key}
                    className={`btn btn-sm join-item ${group === g.key ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setGroup(g.key)}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
              {hourly && hoursSum !== null && dayFigure != null ? (
                <p className="text-xs text-ink-muted mt-2">{publishedHoursText(hoursSum, dayFigure)}</p>
              ) : null}
              {group !== 'everyone' ? (
                <p className="text-xs text-ink-muted mt-2">The figures above count everyone. Counts of people by role are not available yet.</p>
              ) : null}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <DistributionPanel
                title="Straight-line distance (modelled)"
                average={`${MEASURES.average_distance.label}: ${averageText(summary?.average_distance)} km`}
                ee={ee} hour={distHour} group={group} measure="distance" colors={colors}
              />
              <DistributionPanel
                title="Travel time (modelled)"
                average={`${MEASURES.average_travel_time.label}: ${averageText(summary?.average_travel_time)} min`}
                ee={ee} hour={distHour} group={group} measure="travel_time" colors={colors}
              />
              <DistributionPanel
                title="Visit length (modelled)"
                average={`${MEASURES.average_visit_length.label}: ${averageText(summary?.average_visit_length)} min`}
                ee={ee} hour={distHour} group={group} measure="visit_length" colors={colors}
              />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <ModePanel summary={summary} group={group} colors={colors} />
              <DistributionPanel title="Trip purpose (modelled)" ee={ee} hour={distHour} group={group} measure="purpose" colors={colors} />
              {/* Catalogue 1.0 names where arrivals come from in the country's own areas; Estonia's older tables by settlement. */}
              {(ee.distributions ?? []).some((d) => d.measure === 'origin_lau') ? (
                <DistributionPanel title="Origin areas (modelled)" ee={ee} hour={distHour} group={group} measure="origin_lau" colors={colors} />
              ) : (
                <DistributionPanel title="Origin settlements (modelled)" ee={ee} hour={distHour} group={group} measure="origin_settlement" colors={colors} />
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default EeActivityDashboard;
